import PropTypes from 'prop-types'
import { useState } from 'react'
import styled from 'styled-components/macro'

import { IconClose } from '@loadsmart/icons'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { ButtonAction } from 'legacy/components/Button/ButtonAction'
import { Checkbox } from 'legacy/components/Checkbox/Checkbox'
import { I18n } from 'legacy/components/I18n/i18n'
import { Input } from 'legacy/components/InputV2/Input'
import { Modal } from 'legacy/components/ModalV2/Modal'
import { screen } from 'legacy/styles/screen'

import { Portal } from '../Portal'

const Header = styled.header`
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 100%;

  ${screen.md} {
    width: 340px;
  }
`

const CloseButtonWrapper = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${getToken('color-neutral-lighter')};
  border: none;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  cursor: pointer;
  transition: opacity 0.1s linear;

  :hover {
    opacity: 0.7;
  }
`

const StyledIconClose = styled(IconClose)`
  height: 16px;
  width: 16px;
`

const Title = styled.h1`
  font-size: 22px;
  font-weight: bold;
`

const Reason = styled.div`
  align-items: center;
  color: ${getToken('color-neutral')};
  display: flex;
  margin: 8px 0;
`

const Label = styled.label`
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 2;
`

const Button = styled(ButtonAction)`
  cursor: ${({ activated }) => (activated ? 'pointer' : 'default')};
  pointer-events: ${({ activated }) => (activated ? 'auto' : 'none')};
  margin-top: 24px;
  text-transform: uppercase;
  transition: background-color 100ms, color 100ms, opacity 100ms;
  width: 100%;

  &:disabled {
    background: transparent;
    border: 1px solid ${getToken('color-neutral-light')};
    color: ${getToken('color-neutral-light')};
    opacity: 1;
  }

  &:hover {
    opacity: 0.7;
  }
`

const REASONS = {
  appointmentTime: false,
  puDate: false,
  allInRate: false,
  pricePerMile: false,
  facilities: false,
  weight: false,
  deadhead: false,
}

/**
 * get all the reasons that were selected by the user and return its name
 * pushes the custom reasons input if present
 * @param {Array} reasons reasons available to select from checkbox
 * @param {Object} other custom other reasons that the user can input
 */
function getSelectedReasons(reasons, other) {
  let selectedReasons = Object.entries(reasons)
    .filter(([, value]) => value)
    .map(([reason]) => I18n.get(`lb.topOffers.feedback.reasons.${reason}`))

  if (other.checked && other.valid) {
    selectedReasons.push(other.value.trim())
  }

  return selectedReasons
}

function OfferCardFeedbackModal(props) {
  const { isOpen, onClose, onSubmit } = props
  const [reasons, setReasons] = useState(REASONS)
  const [other, setOther] = useState({
    checked: false,
    valid: false,
    value: '',
  })

  const selectedReasons = getSelectedReasons(reasons, other)
  const isFilled = Boolean(selectedReasons.length)

  function handleFeedbackClick(item) {
    setReasons(current => {
      return { ...current, [item]: !current[item] }
    })
  }

  function handleOtherText(event) {
    const {
      target: { value },
    } = event
    const valid = Boolean(value.trim())

    setOther({ checked: valid, valid: valid, value })
  }

  function handleOtherClick() {
    setOther(current => ({ ...current, checked: !current.checked }))
  }

  function handleSubmit(event) {
    event.preventDefault()
    onSubmit(selectedReasons)
  }

  return (
    <Portal>
      <Modal isOpen={isOpen} onClose={onClose}>
        <Modal.Header>
          <Header>
            <Title>{I18n.get('lb.topOffers.feedback.title')}</Title>
            <CloseButtonWrapper type="button" onClick={onClose}>
              <StyledIconClose />
            </CloseButtonWrapper>
          </Header>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            {Object.entries(reasons).map(([name, checked]) => (
              <Reason key={name}>
                <Checkbox
                  id={name}
                  name="feedback"
                  checked={checked}
                  value={name}
                  onChange={() => handleFeedbackClick(name)}
                />
                <Label htmlFor={name}>{I18n.get(`lb.topOffers.feedback.reasons.${name}`)}</Label>
              </Reason>
            ))}
            <Reason>
              <Checkbox
                name="feedback"
                checked={Boolean(other.checked)}
                value={other.value}
                onChange={handleOtherClick}
              />
              <Input
                placeholder={I18n.get('lb.topOffers.feedback.reasons.other')}
                onChange={handleOtherText}
                maxLength={140}
                value={other.value}
              />
            </Reason>
            <Button activated square disabled={!isFilled} width="100%" onClick={handleSubmit}>
              {I18n.get('lb.topOffers.feedback.cta')}
            </Button>
          </form>
        </Modal.Body>
      </Modal>
    </Portal>
  )
}

OfferCardFeedbackModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export { OfferCardFeedbackModal }
